<template>
  <v-container>
    <!-- Sticky header -->
    <div class="topbar">
      <div class="topbar__row">
        <div class="topbar__badge">
          <v-icon size="22">mdi-battery-charging-high</v-icon>
        </div>
        <div class="topbar__title min-width-0">
          <div class="topbar__name">{{ $filters.capitalize($t("battery.label", 2)) }}</div>
          <div class="topbar__sub">
            <span>{{ batteries.length }} {{ $t("battery.label", batteries.length) }}</span>
            <span class="topbar__dot"></span>
            <span>{{ freshCount }} / {{ batteries.length }} {{ $t("production.reporting") }}</span>
            <template v-if="lastRefresh">
              <span class="topbar__dot"></span>
              <span>{{ $t("production.last_refresh") }} {{ $filters.formatTime(lastRefresh) }}</span>
            </template>
          </div>
        </div>
        <div class="topbar__actions">
          <v-btn
            icon
            variant="tonal"
            color="primary"
            size="small"
            :loading="loading"
            :title="$filters.capitalize($t('production.refresh'))"
            @click="updateData"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>
      <v-progress-linear v-if="loading" indeterminate color="primary" height="2" class="topbar__progress"></v-progress-linear>
    </div>

    <!-- Fleet summary -->
    <div class="kpi-grid">
      <v-card flat class="kpi kpi--green">
        <div class="kpi__badge"><v-icon size="20">mdi-battery-high</v-icon></div>
        <div class="min-width-0">
          <div class="kpi__label">{{ $filters.capitalize($t("battery.soc")) }}</div>
          <div class="kpi__value">
            <template v-if="fleetSoc !== null">{{ Math.round(fleetSoc) }} <span class="kpi__unit">%</span></template>
            <span v-else class="text-disabled">&ndash;</span>
          </div>
          <div class="kpi__hint">{{ $filters.formatFloat(totalEnergyLeft) }} / {{ totalCapacity }} {{ $t("unity.kwh") }}</div>
        </div>
      </v-card>
      <v-card flat class="kpi kpi--blue">
        <div class="kpi__badge"><v-icon size="20">mdi-battery-charging</v-icon></div>
        <div class="min-width-0">
          <div class="kpi__label">{{ $filters.capitalize($t("battery.charging")) }}</div>
          <div class="kpi__value">
            {{ $filters.formatFloat(chargingPower) }}
            <span class="kpi__unit">{{ $t("unity.kw") }}</span>
          </div>
          <div class="kpi__hint">{{ chargingCount }} {{ $t("battery.label", chargingCount) }}</div>
        </div>
      </v-card>
      <v-card flat class="kpi kpi--amber">
        <div class="kpi__badge"><v-icon size="20">mdi-battery-arrow-down</v-icon></div>
        <div class="min-width-0">
          <div class="kpi__label">{{ $filters.capitalize($t("battery.discharging")) }}</div>
          <div class="kpi__value">
            {{ $filters.formatFloat(dischargingPower) }}
            <span class="kpi__unit">{{ $t("unity.kw") }}</span>
          </div>
          <div class="kpi__hint">{{ dischargingCount }} {{ $t("battery.label", dischargingCount) }}</div>
        </div>
      </v-card>
      <v-card flat class="kpi kpi--grey">
        <div class="kpi__badge"><v-icon size="20">mdi-battery-sync-outline</v-icon></div>
        <div class="min-width-0">
          <div class="kpi__label">{{ $filters.capitalize($t("battery.idle")) }}</div>
          <div class="kpi__value">
            {{ idleCount }}
            <span class="kpi__unit">/ {{ batteries.length }}</span>
          </div>
          <div class="kpi__hint">{{ staleCount }} {{ $t("production.no_data") }}</div>
        </div>
      </v-card>
    </div>

    <!-- One row per battery -->
    <v-card v-if="batteries.length" flat class="table-card">
      <!-- Phone: one card per battery -->
      <div class="m-list d-md-none">
        <v-card
          v-for="bs in batteries"
          :key="`m-${bs.pk}`"
          flat
          class="m-card"
          :class="{ 'm-card--nodata': stateOf(bs) === 'stale' }"
          @click="openHistory(bs)"
        >
          <span class="m-card__bar" :class="`battery-bar--${stateOf(bs)}`"></span>
          <div class="m-card__body">
            <div class="m-card__head">
              <div class="min-width-0">
                <div class="m-card__name">{{ bs.battery.name }}</div>
                <div class="m-card__sub">{{ bs.battery.tesla_id }} &middot; {{ bs.battery.max_energy }} {{ $t("unity.kwh") }}</div>
              </div>
              <span class="state-pill ml-auto" :class="`state-pill--${stateOf(bs)}`">
                <v-icon size="small">{{ stateIcon(bs) }}</v-icon>
                {{ $filters.capitalize($t(`battery.${stateOf(bs)}`)) }}
              </span>
            </div>
            <div class="soc-cell soc-cell--wide mt-1">
              <div class="soc-cell__bar">
                <div class="soc-cell__fill" :class="`soc-cell__fill--${socColor(socOf(bs))}`" :style="{ width: `${Math.round(socOf(bs) || 0)}%` }"></div>
              </div>
              <span class="soc-cell__value">
                <template v-if="socOf(bs) !== null">{{ Math.round(socOf(bs)) }} %</template>
                <span v-else class="text-disabled">&ndash;</span>
              </span>
            </div>
            <div class="m-card__grid m-card__grid--2 mt-2">
              <div>
                <div class="m-stat__label">{{ $t("battery.energy_left") }}</div>
                <div class="m-stat__value">{{ $filters.formatFloat(bs.energy_left / 1000) }} <span class="m-stat__unit">/ {{ bs.battery.max_energy }} {{ $t("unity.kwh") }}</span></div>
              </div>
              <div>
                <div class="m-stat__label">{{ $t("battery.frequency") }}</div>
                <div class="m-stat__value">
                  <template v-if="hasValue(bs.frequency)">{{ Number(bs.frequency).toFixed(2) }} <span class="m-stat__unit">{{ $t("unity.hz") }}</span></template>
                  <span v-else class="text-disabled">&ndash;</span>
                </div>
              </div>
            </div>
            <div class="m-card__foot">
              <span class="fresh-chip" :class="freshnessClass(bs.time) || 'fresh'">{{ relativeTime(bs.time) }}</span>
              <span class="text-caption text-medium-emphasis ml-2">{{ $filters.formatDateTime(bs.time) }}</span>
            </div>
          </div>
          <v-icon size="small" class="m-card__chevron">mdi-chevron-right</v-icon>
        </v-card>
      </div>

      <v-table class="battery-table d-none d-md-block" density="comfortable">
        <thead>
          <tr>
            <th class="text-left">{{ $filters.capitalize($t("battery.label", 1)) }}</th>
            <th class="text-center">{{ $filters.capitalize($t("generic.status")) }}</th>
            <th class="text-center soc-col">{{ $filters.capitalize($t("battery.soc")) }}</th>
            <th class="text-center">{{ $filters.capitalize($t("battery.energy_left")) }}</th>
            <th class="text-center">{{ $filters.capitalize($t("battery.frequency")) }}</th>
            <th class="text-center">{{ $filters.capitalize($t("production.last_data")) }}</th>
            <th class="row-chevron"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="bs in batteries"
            :key="bs.pk"
            class="battery-row"
            :class="{ 'battery-row--stale': stateOf(bs) === 'stale' }"
            :title="$filters.capitalize($t('battery.history'))"
            @click="openHistory(bs)"
          >
            <td>
              <div class="battery-cell">
                <span class="battery-bar" :class="`battery-bar--${stateOf(bs)}`"></span>
                <div class="min-width-0">
                  <div class="battery-name">{{ bs.battery.name }}</div>
                  <div class="text-caption text-medium-emphasis">{{ bs.battery.tesla_id }} &middot; {{ bs.battery.max_energy }} {{ $t("unity.kwh") }}</div>
                </div>
              </div>
            </td>
            <td class="text-center">
              <span class="state-pill" :class="`state-pill--${stateOf(bs)}`">
                <v-icon size="small">{{ stateIcon(bs) }}</v-icon>
                {{ $filters.capitalize($t(`battery.${stateOf(bs)}`)) }}
              </span>
            </td>
            <td class="text-center">
              <div class="soc-cell">
                <div class="soc-cell__bar">
                  <div class="soc-cell__fill" :class="`soc-cell__fill--${socColor(socOf(bs))}`" :style="{ width: `${Math.round(socOf(bs) || 0)}%` }"></div>
                </div>
                <span class="soc-cell__value">
                  <template v-if="socOf(bs) !== null">{{ Math.round(socOf(bs)) }} %</template>
                  <span v-else class="text-disabled">&ndash;</span>
                </span>
              </div>
            </td>
            <td class="text-center">
              <span class="font-weight-medium">{{ $filters.formatFloat(bs.energy_left / 1000) }}</span>
              <span class="text-caption text-medium-emphasis"> / {{ bs.battery.max_energy }} {{ $t("unity.kwh") }}</span>
            </td>
            <td class="text-center">
              <template v-if="hasValue(bs.frequency)">{{ Number(bs.frequency).toFixed(2) }} <span class="text-caption text-medium-emphasis">{{ $t("unity.hz") }}</span></template>
              <span v-else class="text-disabled">&ndash;</span>
            </td>
            <td class="text-center">
              <span class="fresh-chip" :class="freshnessClass(bs.time) || 'fresh'">{{ relativeTime(bs.time) }}</span>
              <div class="text-caption text-medium-emphasis mt-1">{{ $filters.formatDateTime(bs.time) }}</div>
            </td>
            <td class="row-chevron"><v-icon size="small">mdi-chevron-right</v-icon></td>
          </tr>
        </tbody>
      </v-table>
    </v-card>
    <v-card v-else-if="!loading" flat class="empty-card text-medium-emphasis text-body-2">
      <v-icon size="36" class="mb-2" color="grey">mdi-battery-off-outline</v-icon>
      <div>{{ $filters.capitalize($t("battery.no_data")) }}</div>
    </v-card>
  </v-container>
</template>

<script>
import moment from "moment";
import { isNone } from "@/functions.js";
import utils from "@/mixins/utils";

export default {
  mixins: [utils],

  computed: {
    freshCount() {
      return this.batteries.filter((bs) => this.freshnessClass(bs.time) !== "over_5h").length;
    },

    staleCount() {
      return this.batteries.length - this.freshCount;
    },

    totalEnergyLeft() {
      return this.batteries.reduce((acc, bs) => acc + (Number(bs.energy_left) || 0) / 1000, 0);
    },

    totalCapacity() {
      return this.batteries.reduce((acc, bs) => acc + (Number(bs.battery.max_energy) || 0), 0);
    },

    fleetSoc() {
      const socs = this.batteries.map((bs) => this.socOf(bs)).filter((v) => v !== null);
      return socs.length ? socs.reduce((a, b) => a + b, 0) / socs.length : null;
    },

    chargingPower() {
      return this.batteries
        .filter((bs) => this.stateOf(bs) === "charging")
        .reduce((acc, bs) => acc + Math.abs(this.kw(bs)), 0);
    },

    chargingCount() {
      return this.batteries.filter((bs) => this.stateOf(bs) === "charging").length;
    },

    dischargingPower() {
      return this.batteries
        .filter((bs) => this.stateOf(bs) === "discharging")
        .reduce((acc, bs) => acc + Math.abs(this.kw(bs)), 0);
    },

    dischargingCount() {
      return this.batteries.filter((bs) => this.stateOf(bs) === "discharging").length;
    },

    idleCount() {
      return this.batteries.filter((bs) => this.stateOf(bs) === "idle").length;
    }
  },

  methods: {
    hasValue(value) {
      return !isNone(value) && value !== "";
    },

    kw(bs) {
      return (Number(bs.battery_power) || 0) / 1000;
    },

    // State of charge in %: the reported value, else energy left over the capacity
    socOf(bs) {
      if (this.hasValue(bs.soe)) {
        return Math.min(100, Math.max(0, Number(bs.soe)));
      }
      const max = Number(bs.battery ? bs.battery.max_energy : 0);
      if (max > 0 && this.hasValue(bs.energy_left)) {
        return Math.min(100, Math.max(0, (Number(bs.energy_left) / 1000 / max) * 100));
      }
      return null;
    },

    // battery_power > 0: energy leaves the battery, < 0: it is being charged; no recent status: stale
    stateOf(bs) {
      if (this.freshnessClass(bs.time) === "over_5h") {
        return "stale";
      }
      const p = this.kw(bs);
      if (p > 0) return "discharging";
      if (p < 0) return "charging";
      return "idle";
    },

    stateIcon(bs) {
      return {
        charging: "mdi-battery-charging",
        discharging: "mdi-battery-arrow-down",
        idle: "mdi-battery-sync-outline",
        stale: "mdi-battery-off-outline"
      }[this.stateOf(bs)];
    },



    socColor(soc) {
      if (soc === null) return "grey";
      if (soc < 20) return "error";
      if (soc < 50) return "warning";
      return "success";
    },

    relativeTime(value) {
      const minutes = Math.max(0, Math.round(moment().diff(moment(value), "minutes")));
      if (minutes < 60) return this.$t("production.minutes_ago", { n: minutes });
      const hours = Math.round(minutes / 60);
      if (hours < 48) return this.$t("production.hours_ago", { n: hours });
      return this.$t("production.days_ago", { n: Math.round(hours / 24) });
    },

    freshnessClass(last) {
      if (isNone(last)) return "over_5h";
      const age = moment().diff(moment(last), "hours", true);
      if (age >= 5) return "over_5h";
      if (age >= 3) return "over_3h";
      if (age >= 1) return "over_1h";
      return "";
    },

    openHistory(bs) {
      this.$router.push({ name: "battery_history", query: { battery_id: bs.battery_id || bs.battery.pk } });
    },

    updateData() {
      this.loading = true;
      this.$store
        .dispatch("battery_status/getCurrent")
        .then((items) => {
          this.batteries = Array.isArray(items) ? items : [];
        })
        .catch((error) => {
          console.error("battery status:", error);
          this.batteries = [];
        })
        .finally(() => {
          this.loading = false;
          this.lastRefresh = moment();
        });
    }
  },

  mounted() {
    this.updateData();
    this.intervalId = window.setInterval(() => {
      this.updateData();
    }, 10 * 60 * 1000);
  },

  unmounted() {
    window.clearInterval(this.intervalId);
  },

  data() {
    return {
      intervalId: undefined,
      loading: false,
      lastRefresh: null,
      batteries: []
    };
  }
};
</script>

<style scoped>
/* Freshness scale */
.over_1h {
  color: #c9a800;
}
.over_3h {
  color: #e07b00;
}
.over_5h {
  color: #d32f2f;
}
.fresh {
  color: #2e7d32;
}
.fresh-chip {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 500;
  white-space: nowrap;
  background: color-mix(in srgb, currentColor 12%, white);
}
.min-width-0 {
  min-width: 0;
}

/* Sticky header */
.topbar {
  position: sticky;
  top: var(--v-layout-top, 0px);
  z-index: 5;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}
.topbar::before {
  content: "";
  display: block;
  height: 3px;
  background: linear-gradient(90deg, #0093bf 0%, #1aa3a8 55%, #43a047 100%);
}
.topbar__row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
}
.topbar__badge {
  width: 40px;
  height: 40px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #e3f4e4;
  color: #2e7d32;
}
.topbar__title {
  flex: 1 1 200px;
}
.topbar__name {
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
}
.topbar__sub {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
  overflow: hidden;
}
.topbar__dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.3);
  flex-shrink: 0;
}
.topbar__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
}
.topbar__progress {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
}

/* Fleet summary */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-top: 10px;
}
.kpi {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.kpi__badge {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.kpi--green .kpi__badge {
  background: #e3f4e4;
  color: #2e7d32;
}
.kpi--blue .kpi__badge {
  background: #dff3f9;
  color: #0093bf;
}
.kpi--amber .kpi__badge {
  background: #fff3d6;
  color: #e08a00;
}
.kpi--grey .kpi__badge {
  background: #eeeeee;
  color: #616161;
}
.kpi__label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.kpi__value {
  margin-top: 2px;
  font-size: 1.65rem;
  font-weight: 700;
  line-height: 1.15;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.87);
}
.kpi__unit {
  font-size: 0.85rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.5);
}
.kpi__hint {
  margin-top: 2px;
  min-height: 1.1em;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Battery table, the look of the wind and solar tables */
.table-card {
  margin-top: 10px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}
.battery-table :deep(th) {
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.05em;
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.55) !important;
  background: #fff;
}
.battery-table :deep(th.soc-col) {
  width: 220px;
}
.battery-table :deep(td) {
  font-variant-numeric: tabular-nums;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04) !important;
}
.battery-table :deep(tr.battery-row) {
  cursor: pointer;
}
.battery-table :deep(tr.battery-row:hover td) {
  background-color: #e8f4f9;
}
/* Clickable rows: a chevron at the end that lights up with the row */
.row-chevron {
  width: 36px;
  padding: 0 8px 0 0 !important;
  text-align: right;
}
.row-chevron .v-icon {
  color: rgba(0, 0, 0, 0.26);
  transition: color 0.15s ease, transform 0.15s ease;
}
tr:hover .row-chevron .v-icon {
  color: rgb(var(--v-theme-primary));
  transform: translateX(2px);
}

.battery-table :deep(tr.battery-row--stale td) {
  background-color: #fdecec;
}
.battery-table :deep(tr.battery-row--stale:hover td) {
  background-color: #fadcdc;
}
.battery-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.battery-bar {
  display: inline-block;
  width: 4px;
  height: 30px;
  border-radius: 2px;
  flex-shrink: 0;
  background: #9e9e9e;
}
.battery-bar--charging {
  background: #0093bf;
}
.battery-bar--discharging {
  background: #2e7d32;
}
.battery-bar--stale {
  background: #d32f2f;
}
.battery-name {
  font-weight: 600;
  white-space: nowrap;
}
.state-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  background: color-mix(in srgb, currentColor 12%, white);
}
.state-pill--charging {
  color: #0093bf;
}
.state-pill--discharging {
  color: #2e7d32;
}
.state-pill--idle {
  color: #616161;
}
.state-pill--stale {
  color: #d32f2f;
}
.soc-cell {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  width: 200px;
}
.soc-cell--wide {
  display: flex;
  width: 100%;
}
.soc-cell__bar {
  flex: 1;
  height: 8px;
  border-radius: 4px;
  background: #eceff1;
  overflow: hidden;
}
.soc-cell__fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
  background: #9e9e9e;
}
.soc-cell__fill--success {
  background: #2e7d32;
}
.soc-cell__fill--warning {
  background: #e08a00;
}
.soc-cell__fill--error {
  background: #d32f2f;
}
.soc-cell__value {
  min-width: 3em;
  text-align: right;
  font-weight: 700;
}
.empty-card {
  margin-top: 10px;
  padding: 32px 16px;
  text-align: center;
  border-radius: 12px;
  border: 1px dashed rgba(0, 0, 0, 0.12);
}

/* Phone layout: cards instead of the table */
.m-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
}
.m-station {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  padding: 6px 4px 2px;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(0, 0, 0, 0.6);
}
.m-station__stat {
  margin-left: auto;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
  white-space: nowrap;
}
.m-card {
  display: flex;
  align-items: stretch;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  cursor: pointer;
}
.m-card--nodata {
  background: #fdecec;
}
.m-card__bar {
  width: 4px;
  border-radius: 2px;
  background: currentColor;
  flex-shrink: 0;
}
.m-card__body {
  flex: 1;
  min-width: 0;
}
.m-card__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}
.m-card__name {
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.m-card__sub {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.55);
}
.m-card__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 4px 10px;
}
.m-card__grid--2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.m-stat__label {
  font-size: 0.66rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.5);
  white-space: nowrap;
}
.m-stat__value {
  font-size: 0.95rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.m-stat__unit {
  font-size: 0.7rem;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.5);
}
.m-card__foot {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}
.m-card__chevron {
  align-self: center;
  color: rgba(0, 0, 0, 0.26);
}

@media (max-width: 700px) {
  /* Tiles one per line on a phone */
  .kpi-grid {
    grid-template-columns: 1fr;
  }
  .kpi {
    padding: 10px 12px;
    gap: 10px;
  }
  .kpi__label {
    white-space: normal;
    line-height: 1.2;
  }
  .kpi__hint {
    white-space: normal;
    line-height: 1.2;
  }
  .topbar__row {
    flex-wrap: wrap;
  }
}
</style>
